import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useCreateThread,
  useThreadForAnchor,
  useThreadForContact,
  useThreadForConversation,
  useThreadForJob,
  useThreadForTask,
} from '../../hooks/useThreads';
import { trpc } from '../../lib/trpc';
import type { User } from '../../types/comments';
import { CommentThread } from './CommentThread';

/** Which entity a discussion thread hangs off of. */
export type ThreadTarget =
  | { type: 'contact'; contactId: number }
  | { type: 'anchor'; anchorId: number }
  | { type: 'task'; taskId: number }
  | { type: 'job'; jobId: number }
  | { type: 'emailThread'; conversationId: number };

interface EntityCommentThreadProps {
  target: ThreadTarget;
  /** Optional — resolved from the workspace member list when omitted. */
  users?: User[];
  /** Optional — resolved from the authed user + member list when omitted. */
  currentUser?: User;
  isReadOnly?: boolean;
}

/**
 * Pre-wires {@link CommentThread} to a contact / task / email-thread's discussion.
 *
 * There is deliberately **no "Start Discussion" wall**: when the entity has no
 * thread yet, the live composer renders immediately (Reciprocity — the tool
 * arrives before the ask) and the user's first message lazily creates the thread
 * (IKEA/Endowment — the thread *is* their message, not an empty ceremony).
 *
 * Replaces the old ContactCommentThread / TaskCommentThread / EmailThreadCommentThread
 * trio, which were byte-for-byte identical apart from the id field.
 */
export function EntityCommentThread({
  target,
  users: externalUsers,
  currentUser: externalCurrentUser,
  isReadOnly = false,
}: EntityCommentThreadProps) {
  // Only the query matching `target.type` is enabled; the rest stay gated off.
  const contactQuery = useThreadForContact(target.type === 'contact' ? target.contactId : null);
  const anchorQuery = useThreadForAnchor(target.type === 'anchor' ? target.anchorId : null);
  const taskQuery = useThreadForTask(target.type === 'task' ? target.taskId : null);
  const jobQuery = useThreadForJob(target.type === 'job' ? target.jobId : null);
  const emailQuery = useThreadForConversation(
    target.type === 'emailThread' ? target.conversationId : null
  );
  const activeQuery =
    target.type === 'contact'
      ? contactQuery
      : target.type === 'anchor'
        ? anchorQuery
        : target.type === 'task'
          ? taskQuery
          : target.type === 'job'
            ? jobQuery
            : emailQuery;
  const { data: thread, isLoading: isLoadingThread } = activeQuery;

  const createThreadMutation = useCreateThread();

  // Resolve identity/roster locally when the host didn't supply them.
  const meQuery = trpc.auth.me.useQuery(undefined, { enabled: !externalCurrentUser });
  const membersQuery = trpc.workspace.listMembers.useQuery(undefined, { enabled: !externalUsers });

  const currentUser: User | null =
    externalCurrentUser ||
    (meQuery.data
      ? (() => {
          const m = membersQuery.data?.find((member) => member.id === meQuery.data.uid);
          return m
            ? { id: m.id, fullName: m.fullName || m.email, email: m.email, avatarUrl: null }
            : { id: meQuery.data.uid, fullName: 'Me', email: '', avatarUrl: null };
        })()
      : null);

  const users: User[] =
    externalUsers ||
    (membersQuery.data
      ? membersQuery.data.map((m) => ({
          id: m.id,
          fullName: m.fullName || m.email,
          email: m.email,
          avatarUrl: null,
        }))
      : []);

  if (isLoadingThread || (meQuery.isLoading && !externalCurrentUser)) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center p-4 text-xs text-muted-foreground">
        Sign in to view internal chat messages.
      </div>
    );
  }

  // Mints the thread on the user's first message; CommentThread posts that
  // message to the returned id through the normal comment pipeline.
  const createThread = async (): Promise<number> => {
    const input =
      target.type === 'contact'
        ? ({ targetType: 'contact', contactId: target.contactId } as const)
        : target.type === 'anchor'
          ? ({ targetType: 'anchor', anchorId: target.anchorId } as const)
          : target.type === 'task'
            ? ({ targetType: 'task', taskId: target.taskId } as const)
            : target.type === 'job'
              ? ({ targetType: 'job', jobId: target.jobId } as const)
              : ({ targetType: 'emailThread', conversationId: target.conversationId } as const);

    const res = await createThreadMutation.mutateAsync(input, {
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to start discussion'),
    });
    if (!res.thread?.id) throw new Error('Failed to start discussion');
    return res.thread.id;
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <CommentThread
        threadId={thread?.id ?? null}
        onCreateThread={createThread}
        variant="sidebar"
        users={users}
        currentUser={currentUser}
        isReadOnly={isReadOnly}
        onError={(message) => toast.error(message)}
      />
    </div>
  );
}
